import { useEffect, useRef, useState } from 'react';
import { useConnectCredentialsStore } from '../stores/session/connectCredentialsStore';
import { useAppSessionStore } from '../stores/session/appSessionStore';
import { useSessionStore } from '../stores/session/sessionStore';
import { useTenantStore, getCiu } from '../stores/tenantStore';
import { useAuthStore } from '../stores/authStore';
import { useLanguageStore } from '../stores/languageStore';
import { useThemeStore } from '../stores/themeStore';
import { oidcUserManager } from '../services/oidcService';
import { refreshAcceoConnectToken, getCurrentUser } from '../services/acceoConnectService';
import { exchangePortalNonce } from '../services/portalAuthService';
import { requestPortalRenonce } from '../utils/portal/portalRenonce';
import { syncService } from '../services/syncService';
import { refreshAppSession, ensureFreshAppToken, establishBrowserAppSession, refreshDesktopAppSession } from '../services/appSession';
import { getDesktopAssertion } from '../services/desktopAssertion';
import { tryApplyBridgeLaunchContext } from '../services/bridgeLaunchContext';
import { useSyncStore } from '../stores/syncStore';
import { scheduleAcceoConnectRefresh, scheduleOidcRefresh } from '../utils/auth/authRefreshScheduler';

export interface AuthGuardState { ready: boolean; error: boolean; initialSyncRequired: boolean; }

/**
 * Ensures a usable app session (the API bearer for every request) exists for a desktop/OIDC session.
 * Reuses and refreshes the persisted one when present; if its refresh token is dead — or none exists —
 * mints a fresh one from the live OIDC token. Desktop can always re-mint (it holds an OIDC session),
 * unlike portal which must re-nonce through the host.
 * Returns false when no tenant (ciu) is resolved yet (caller should fall through to the redirect path),
 * or when the mint itself fails (network error / non-2xx) — the caller falls through to signinRedirect.
 */
async function ensureBrowserAppSession(oidcAccessToken: string): Promise<boolean> {
    const ciu = getCiu();
    const tenantCode = useTenantStore.getState().tenantCode;
    if (ciu === null || tenantCode === null) return false;
    if (useAppSessionStore.getState().appRefreshToken) {
        const existing = await ensureFreshAppToken();
        if (existing) return true;
    }
    return establishBrowserAppSession(oidcAccessToken, ciu, tenantCode);
}

/**
 * Returns an AuthGuardState once the AcceoConnect session is confirmed valid.
 * When a portal_nonce query param is present, exchanges it first (F-01.10).
 * When a plain nonce query param is present, performs a post-auth initial-sync check.
 * When the stored token is expired, attempts a silent refresh using the OIDC session
 * before falling back to a full OIDC redirect.
 */

export function useAuthGuard(): AuthGuardState {
    const { acceoConnectToken, isExpired, setAcceoConnectToken, setSaasSessionToken, setPortalIsToken, portalIsToken } = useConnectCredentialsStore();
    const { setAppSession, appRefreshToken } = useAppSessionStore();
    const { setSessionKind, sessionKind, setReconnecting, setConnectSessionFailed } = useSessionStore();
    const { tenantCode, setTenant } = useTenantStore();
    const { setDisplayName } = useAuthStore();
    const setLang = useLanguageStore((s) => s.setLang);
    const seedServerPreference = useLanguageStore((s) => s.seedServerPreference);
    const setDark = useThemeStore((s) => s.setDark);
    const initialSyncRequired = useSyncStore(s => s.initialSyncRequired);
    const setInitialSyncRequired = useSyncStore(s => s.setInitialSyncRequired);
    const [ready, setReady] = useState(false);
    const [hardError, setHardError] = useState(false);
    // Tracks whether the session was established via portal nonce exchange.
    // When true, token validity is guaranteed by the nonce exchange itself.
    const [nonceAuthenticated, setNonceAuthenticated] = useState(false);
    const checking = useRef(false);
    const acceoConnectRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const oidcRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const oidcMountedRef = useRef(true);

    useEffect(() => {
        if (checking.current) return;

        // Re-assert "mounted" on every effect run, not just the first mount. The deps
        // include `token`, and every successful refresh calls setAcceoConnectToken(), which triggers
        // the cleanup (oidcMountedRef = false) then a re-run. Without resetting it here the
        // ref stays false, scheduleOidcRefresh early-returns forever, and OIDC silent renewal
        // dies after the first refresh — starving the AcceoConnect refresh of a valid OIDC
        // token (~2h later) since automaticSilentRenew is off.
        oidcMountedRef.current = true;
        checking.current = true;

        void (async () => {
            let sessionEstablished = false;
            try {
                // Nonce branch — must run before any storage-dependent auth check (F-01.10)
                const params = new URLSearchParams(window.location.search);

                // Acomba Desktop launch: ?src=<assertion> on any route (e.g. the root) means the user
                // arrived directly from acceo_web.exe bypassing /desktop. Redirect so the OIDC flow
                // carries the assertion in state rather than losing it to the URL strip.
                const src = params.get('src');
                if (src) {
                    window.location.replace(`/desktop?src=${encodeURIComponent(src)}`);
                    return;
                }
                const portalNonce = params.get('portal_nonce');
                // Plain nonce param indicates a nonce-based login entry point (e.g. from an external link).
                // Its presence gates the post-auth initial-sync check below.
                const hasNonce = params.has('nonce');
                if (portalNonce) {
                    try {
                        const lang = params.get('lang');
                        const theme = params.get('theme');

                        const portalAuth = await exchangePortalNonce(portalNonce);
                        setAcceoConnectToken(portalAuth.access_token, portalAuth.expires_in);
                        setSaasSessionToken(portalAuth.saasSessionToken ?? null);
                        setPortalIsToken(portalAuth.is_token ?? null);
                        // App session: our own API bearer + refresh token. From here on the
                        // API bearer is this token, not the host IS JWT — so the session survives the
                        // IS JWT's expiry and renews via /connect/portal-refresh.
                        if (portalAuth.app_refresh_token)
                            setAppSession(portalAuth.app_access_token, portalAuth.app_refresh_token, portalAuth.app_expires_in);
                        setSessionKind('portal');
                        // A fresh exchange means the (possibly cold) session is recovered, so no
                        // recovery overlay may outlive it. A launch arrives in a new page context and
                        // the flags are in-memory only, so this normally finds them already false —
                        // it is here so the invariant holds however the branch is reached.
                        setReconnecting(false);
                        setConnectSessionFailed(false);
                        setTenant(portalAuth.tenantCode, portalAuth.companyName ?? '', portalAuth.ciu);
                        // Apply lang/theme after tokens are set — setLang fires userLanguageService.setLanguage
                        // which requires auth headers; calling it before setAcceoConnectToken/setPortalIsToken causes a 401.
                        if (lang === 'fr' || lang === 'en') setLang(lang);
                        if (theme === 'dark' || theme === 'light') setDark(theme === 'dark');
                        const currentUser = await getCurrentUser();
                        setDisplayName(currentUser?.displayName ?? null);
                        seedServerPreference();
                        // Strip portal_nonce, lang, and theme from URL without adding a history entry (F-01.3)
                        const clean = new URL(window.location.href);
                        clean.searchParams.delete('portal_nonce');
                        clean.searchParams.delete('lang');
                        clean.searchParams.delete('theme');
                        window.history.replaceState(null, '', clean.toString());
                        setNonceAuthenticated(true);
                        try {
                            const status = await syncService.getStatus();
                            if (!status.hasEverSynced) {
                                setInitialSyncRequired(true); // triggers InitialSync overlay in RootLayout
                            }
                        } catch {
                            // Fail-open: hasEverSynced treated as true, route to Home normally
                        }
                        setReady(true);
                        sessionEstablished = true;
                        return;
                    } catch (e) {
                        console.error('Portal nonce exchange failed:', e);
                        if (window.self !== window.top) {
                            // Cross-origin iframe: OIDC redirect would fail silently; show error instead (F-01.11b)
                            setHardError(true);
                            return;
                        }
                        // Top-level window: fall through to OIDC flow (F-01.11a)
                    }
                }

                // Acomba Desktop session branch — handles page reloads with a persisted desktop app
                // session. Desktop has no AcceoConnect and no portal iframe. The app refresh token
                // gates readiness; on expiry the session is refreshed via /connect/desktop-refresh.
                // No OIDC redirect is used for refresh: the stored app refresh token is sufficient.
                if (sessionKind === 'desktop' && appRefreshToken) {
                    // The stashed assertion is the only launch token a plain reload has. Enough for the
                    // bridge coordinates, which the bridge still demands after a reload; isStaleCopy is
                    // left untouched (the last value set at login or the desktop fast path stands).
                    const assertion = getDesktopAssertion();
                    if (!tryApplyBridgeLaunchContext(assertion)) {
                        // Reached only when the stash names a different société than this window has pinned.
                        try {
                            await oidcUserManager.signinRedirect();
                        } catch (e) {
                            console.error('signinRedirect failed:', e);
                        }
                        return;
                    }
                    const refreshResult = await refreshDesktopAppSession();
                    if (refreshResult) {
                        setReconnecting(false);
                        setConnectSessionFailed(false);
                        setReady(true);
                        sessionEstablished = true;
                        return;
                    }
                    // Desktop refresh token is dead — the session has truly expired. Redirect the
                    // user through OIDC so they can re-authenticate. The /desktop page will not be
                    // entered directly here (no ?src param), so it falls through to signinRedirect.
                    try {
                        await oidcUserManager.signinRedirect(assertion !== null ? { state: { src: assertion } } : undefined);
                    } catch (e) {
                        console.error('signinRedirect failed:', e);
                    }
                    return;
                }

                // AcombaGO browser session branch — handles page reloads of a standalone-browser AcombaGO
                // session (app token minted at login). The app refresh token gates readiness; the
                // AcceoConnect token + OIDC token are kept fresh lazily (buildTenantHeaders + scheduler),
                // so they don't gate readiness. No re-nonce — that is portal-only; this flow holds a live
                // OIDC session and can re-mint the app session directly when its refresh token is dead.
                if (sessionKind === 'browser') {
                    if (appRefreshToken) {
                        // ensureFreshAppToken returns the current token untouched when it is still fresh
                        // (e.g. the immediate post-login render) and only rotates when near expiry.
                        const appToken = await ensureFreshAppToken();
                        if (appToken) {
                            setReconnecting(false);
                            setConnectSessionFailed(false);
                            setReady(true);
                            sessionEstablished = true;
                            return;
                        }
                    }
                    // App refresh token missing/dead — re-mint from the live (or silently-renewed) OIDC session.
                    const oidcUser = await oidcUserManager.getUser();
                    const oidcToken = oidcUser && !oidcUser.expired ? oidcUser.access_token : null;
                    if (oidcToken && await ensureBrowserAppSession(oidcToken)) {
                        setReady(true);
                        sessionEstablished = true;
                        return;
                    }
                    try {
                        const silentUser = await oidcUserManager.signinSilent();
                        if (silentUser?.access_token && await ensureBrowserAppSession(silentUser.access_token)) {
                            setReady(true);
                            sessionEstablished = true;
                            return;
                        }
                    } catch {
                        // Silent renewal failed — fall through to a full OIDC redirect.
                    }
                    try {
                        await oidcUserManager.signinRedirect();
                    } catch (e) {
                        console.error('signinRedirect failed:', e);
                    }
                    return;
                }

                // Portal session branch — handles page reloads within the portal iframe.
                // Desktop sessions now also carry an app refresh token, so the session kind (set at
                // nonce exchange) is what marks an iframe portal flow that must never attempt an OIDC
                // redirect — not the mere presence of a token.
                if (sessionKind === 'portal') {
                    // Readiness hinges on our app session (the API bearer), not the host IS JWT.
                    if (appRefreshToken) {
                        const appToken = await refreshAppSession();
                        if (appToken) {
                            // Best-effort: keep the AcceoConnect token fresh for sync/file features.
                            // Its failure (host IS JWT expired) no longer blocks our-DB features.
                            // Only refresh when the token is actually stale: a successful refresh calls
                            // setAcceoConnectToken with a brand-new token string, which changes this effect's `acceoConnectToken`
                            // dependency and re-runs it. Without the staleness guard the re-run refreshes
                            // again — an unbounded loop that re-mints the token several times per second.
                            if (tenantCode && portalIsToken && isExpired()) {
                                try {
                                    const result = await refreshAcceoConnectToken(portalIsToken, tenantCode);
                                    if (result) {
                                        setAcceoConnectToken(result.access_token, result.expires_in);
                                        setSaasSessionToken(result.saasSessionToken ?? null);
                                    }
                                } catch {
                                    // AcceoConnect token could not be refreshed (recovered via the server-side session / re-nonce).
                                }
                            }
                            // A usable portal session means recovery is complete, so drop any
                            // reconnecting/failed state an earlier re-nonce left behind. This effect
                            // re-runs on token/tenant changes, so it can land while a re-nonce is
                            // still outstanding — that is the case this clear actually serves.
                            setReconnecting(false);
                            setConnectSessionFailed(false);
                            setReady(true);
                            sessionEstablished = true;
                            return;
                        }
                        // The app refresh token is dead — expired, or revoked because a rotated
                        // one was presented twice (AppSessionService revokes every session for the
                        // subject when that happens). A portal session is not stuck though: a fresh
                        // launch nonce mints a brand-new app session, which is precisely the recovery
                        // the host exists to provide. Ask for one instead of dead-ending on an error
                        // the operator can do nothing about. Only when there is no host to ask — no
                        // trusted origin configured — is the error the honest answer, since the
                        // request would otherwise be dropped and leave the app silently blank.
                        if (!requestPortalRenonce()) setHardError(true);
                        return;
                    }

                    // Legacy portal session (established before app tokens existed): prior behavior.
                    if (acceoConnectToken && !isExpired()) {
                        setReady(true);
                        sessionEstablished = true;
                        return;
                    }
                    if (tenantCode && portalIsToken) {
                        try {
                            const result = await refreshAcceoConnectToken(portalIsToken, tenantCode);
                            if (result) {
                                setAcceoConnectToken(result.access_token, result.expires_in);
                                setSaasSessionToken(result.saasSessionToken ?? null);
                                setReady(true);
                                return;
                            }
                        } catch {
                            // IS JWT expired — cannot silently refresh
                        }
                    }
                    // Portal refresh failed — show error rather than a blank OIDC redirect
                    setHardError(true);
                    return;
                }

                const oidcUser = await oidcUserManager.getUser();

                if (acceoConnectToken && !isExpired() && oidcUser?.access_token && !oidcUser.expired) {
                    // The API bearer is our app token now — establish it before any apiClient call below.
                    if (await ensureBrowserAppSession(oidcUser.access_token)) {
                        const currentUser = await getCurrentUser();
                        setDisplayName(currentUser?.displayName ?? null);
                        seedServerPreference();
                        if (hasNonce) {
                            try {
                                const status = await syncService.getStatus();
                                if (!status.hasEverSynced) {
                                    setInitialSyncRequired(true); // triggers InitialSync overlay in RootLayout
                                }
                            } catch {
                                // Fail-open: hasEverSynced treated as true, route to Home normally
                            }
                        }
                        setReady(true);
                        sessionEstablished = true;
                        return;
                    }
                    // No app session yet (no tenant resolved) — fall through to the refresh/redirect path.
                }

                if (oidcUser && !oidcUser.expired && oidcUser.access_token && tenantCode) {
                    try {
                        const result = await refreshAcceoConnectToken(oidcUser.access_token, tenantCode);
                        if (result && await ensureBrowserAppSession(oidcUser.access_token)) {
                            setAcceoConnectToken(result.access_token, result.expires_in);
                            setSaasSessionToken(result.saasSessionToken ?? null);
                            setReady(true);
                            sessionEstablished = true;
                            return;
                        }
                    } catch {
                        // Silent refresh failed — fall through to full OIDC redirect.
                    }
                }

                // Before forcing a full redirect, try a silent OIDC renewal.
                // The local OIDC user may be stale (e.g. expires_at manually zeroed)
                // while the OIDC server session is still alive.
                if (tenantCode) {
                    try {
                        const silentUser = await oidcUserManager.signinSilent();
                        if (silentUser?.access_token) {
                            const result = await refreshAcceoConnectToken(silentUser.access_token, tenantCode);
                            if (result && await ensureBrowserAppSession(silentUser.access_token)) {
                                setAcceoConnectToken(result.access_token, result.expires_in);
                                setSaasSessionToken(result.saasSessionToken ?? null);
                                const currentUser = await getCurrentUser();
                                setDisplayName(currentUser?.displayName ?? null);
                                seedServerPreference();
                                setReady(true);
                                sessionEstablished = true;
                                return;
                            }
                        }
                    } catch {
                        // Silent renewal failed — fall through to full redirect.
                    }
                }

                try {
                    await oidcUserManager.signinRedirect();
                } catch (e) {
                    console.error('signinRedirect failed:', e);
                }
            } finally {
                checking.current = false;

                if (sessionEstablished) {
                    scheduleAcceoConnectRefresh(acceoConnectRefreshTimerRef);
                    await scheduleOidcRefresh(oidcRefreshTimerRef, oidcMountedRef);
                }
            }
        })();

        return () => {
            oidcMountedRef.current = false;
            if (acceoConnectRefreshTimerRef.current) {
                clearTimeout(acceoConnectRefreshTimerRef.current);
                acceoConnectRefreshTimerRef.current = null;
            }
            if (oidcRefreshTimerRef.current) {
                clearTimeout(oidcRefreshTimerRef.current);
                oidcRefreshTimerRef.current = null;
            }
        };
        // isExpired is a stable Zustand fn and not a meaningful dep.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [acceoConnectToken, tenantCode]);

    // For nonce-authenticated sessions, the token is confirmed valid at exchange time.
    // For app (portal) sessions, the app refresh token gates readiness — the API bearer rides it,
    // not the AcceoConnect token. For OIDC sessions, re-check the AcceoConnect token + expiry.
    const isReady = nonceAuthenticated || (ready && (!!appRefreshToken || (!!acceoConnectToken && !isExpired())));
    return { ready: isReady, error: hardError, initialSyncRequired };
}
